<template>
  <div v-if="hasAccess" class="space-y-6 pb-10">
    <page-content>
      <section class="space-y-6">
        <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <dashboard-date-filters
            class="xl:flex-1"
            :quick-ranges="quickRanges"
            :active-range-id="activeQuickRangeId"
            :date-range="dateRange"
            :max-date="maxDate"
            @select-range="applyQuickRange"
            @show-full="showFull"
            @update:date-range="handleDateRangeUpdate"
          />

          <div class="tracking-filters inline-flex flex-wrap items-center gap-3 rounded-2xl border border-blue-200 bg-white px-4 py-3 shadow-sm xl:min-w-[760px]">
            <div class="tracking-filter-field tracking-filter-field--wide">
              <t-rich-select
                :key="domainSelectKey"
                class="tracking-filter-control"
                multiple
                :close-on-select="false"
                :options="domainOptions"
                value-attribute="uuid"
                text-attribute="label"
                placeholder="Tous les domaines"
                v-model="selectedDomainUuids"
              >
                <template slot="label" slot-scope="{ options, className }">
                  <span :class="className">
                    {{ domainSelectionLabel }}
                  </span>
                </template>
              </t-rich-select>
            </div>

            <div class="tracking-filter-field">
              <t-select
                class="tracking-filter-control"
                :options="typeOptions"
                value-attribute="value"
                text-attribute="label"
                v-model="selectedType"
              />
            </div>

            <div class="tracking-filter-field">
              <t-select
                class="tracking-filter-control"
                :options="duplicationOptions"
                value-attribute="value"
                text-attribute="label"
                v-model="selectedDuplication"
              />
            </div>
          </div>
        </div>

        <div class="published-overview-grid">
          <section class="published-space published-space--expert bg-white rounded-2xl shadow-sm border border-blue-200">
            <header class="published-space-header">
              <div class="tracking-title-row">
                <h2 class="text-lg font-semibold text-gray-900">Contenus experts</h2>
                <button
                  type="button"
                  class="tracking-info-button"
                  title="Suivi mois par mois"
                  @click="openMonthlyTracking"
                >
                  +
                </button>
              </div>
              <article class="tracking-period-stat tracking-period-stat--expert">
                <span>All période</span>
                <strong>{{ formatNumber(totals.expertContents) }}</strong>
              </article>
            </header>

            <div class="published-card-body">
              <dl class="published-breakdown">
                <div class="published-breakdown-row published-breakdown-row--article">
                  <dt>Article</dt>
                  <dd>{{ formatNumber(totals.expertArticleContents) }}</dd>
                </div>
                <div class="published-breakdown-row published-breakdown-row--faq">
                  <dt>FAQ</dt>
                  <dd>{{ formatNumber(totals.expertFaqContents) }}</dd>
                </div>
                <div class="published-breakdown-row published-breakdown-row--survey">
                  <dt>Sondage</dt>
                  <dd>{{ formatNumber(totals.expertSurveyContents) }}</dd>
                </div>
              </dl>

              <article v-if="!hasDomainFilter" class="published-average-card">
                <div class="published-average-circle">
                  <span>{{ formatDecimal(averageExpertContentsPerDomain) }}</span>
                </div>
                <p>Moyenne par domaine</p>
              </article>
            </div>
          </section>

          <section class="published-space published-space--media bg-white rounded-2xl shadow-sm border border-blue-200">
            <header class="published-space-header">
              <h2 class="text-lg font-semibold text-gray-900">Contenus média</h2>
              <article class="tracking-period-stat tracking-period-stat--media">
                <span>All période</span>
                <strong>{{ formatNumber(totals.mediaContents) }}</strong>
              </article>
            </header>

            <div class="published-card-body">
              <dl class="published-breakdown published-breakdown--compact">
                <div class="published-breakdown-row published-breakdown-row--article">
                  <dt>Article</dt>
                  <dd>{{ formatNumber(totals.mediaArticleContents) }}</dd>
                </div>
                <div class="published-breakdown-row published-breakdown-row--survey">
                  <dt>Sondage</dt>
                  <dd>{{ formatNumber(totals.mediaSurveyContents) }}</dd>
                </div>
              </dl>

              <article v-if="!hasDomainFilter" class="published-average-card">
                <div class="published-average-circle">
                  <span>{{ formatDecimal(averageMediaContentsPerDomain) }}</span>
                </div>
                <p>Moyenne par domaine</p>
              </article>
            </div>
          </section>

          <section class="published-space published-space--duplication published-duplication-card bg-white rounded-2xl shadow-sm border border-blue-200">
            <header class="published-space-header">
              <h2 class="text-lg font-semibold text-gray-900">Taux de duplication</h2>
            </header>
            <div class="published-duplication-body">
              <strong>{{ formatPercent(totals.duplicationRate) }}</strong>
              <span>{{ formatNumber(totals.duplicatedContents) }} dupliqués / {{ formatNumber(totals.publishedContents) }} contenus</span>
            </div>
          </section>

          <button
            type="button"
            class="published-space published-space--unscheduled published-unscheduled-card bg-white rounded-2xl shadow-sm border border-blue-200"
            :aria-label="`Voir les ${formatNumber(domainsWithoutScheduledContents.count)} comptes sans contenus programmés cette semaine`"
            @click="openDomainsWithoutScheduledContents"
          >
            <span class="published-space-header">
              <h2 class="text-lg font-semibold text-gray-900">Compte sans contenus programmés cette semaine</h2>
            </span>
            <span class="published-unscheduled-body">
              <strong>{{ formatNumber(domainsWithoutScheduledContents.count) }}</strong>
              <span>{{ currentWeekSchedulePeriodLabel }}</span>
              <span class="published-unscheduled-link">Voir les domaines</span>
            </span>
          </button>
        </div>

        <section class="rounded-2xl border border-blue-200 bg-white shadow-sm">
          <header class="border-b border-gray-200 px-5 py-4">
            <div class="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Détail client</h2>
                <p class="mt-1 text-sm text-gray-600">
                  Publications (articles, FAQ, sondages) avec distinction dupliqués/originaux.
                </p>
              </div>
              <p v-if="isLoading" class="text-sm text-gray-600">Chargement des données...</p>
            </div>
          </header>
          <div class="px-2 py-4">
            <m-v-p-table
              v-if="gridOptions"
              :options="gridOptions"
              :grid-data="gridData"
              status-bar-type="total"
              @gridReady="onGridReady"
            />
          </div>
        </section>

        <div
          v-if="isMonthlyTrackingOpen"
          class="tracking-modal-overlay"
          @click.self="closeMonthlyTracking"
        >
          <div class="tracking-modal-card tracking-modal-card--wide">
            <header class="tracking-modal-header">
              <div>
                <h3 class="tracking-modal-title">Suivi mois par mois</h3>
                <p class="tracking-modal-subtitle">
                  Contenus experts, média et taux de duplication sur l'année sélectionnée.
                </p>
              </div>
              <div class="tracking-modal-domain-filter">
                <label>Client</label>
                <t-rich-select
                  :key="`${domainSelectKey}::monthly`"
                  class="tracking-filter-control"
                  multiple
                  :close-on-select="false"
                  :options="domainOptions"
                  value-attribute="uuid"
                  text-attribute="label"
                  placeholder="Tous les domaines"
                  v-model="selectedDomainUuids"
                >
                  <template slot="label" slot-scope="{ options, className }">
                    <span :class="className">
                      {{ domainSelectionLabel }}
                    </span>
                  </template>
                </t-rich-select>
              </div>
              <button
                type="button"
                class="tracking-modal-close"
                @click="closeMonthlyTracking"
              >
                ×
              </button>
            </header>

            <div class="tracking-modal-table-wrapper tracking-monthly-table-wrapper">
              <table class="tracking-modal-table tracking-monthly-table">
                <thead>
                  <tr>
                    <th>
                      <select v-model.number="selectedTrackingYear" class="tracking-year-select">
                        <option v-for="year in trackingYearOptions" :key="year" :value="year">
                          {{ year }}
                        </option>
                      </select>
                    </th>
                    <th v-for="month in monthlyTrackingRows" :key="month.key" class="is-right">
                      {{ month.label }}
                    </th>
                  </tr>
                </thead>
                <tbody v-if="!isMonthlyTrackingLoading">
                  <tr v-for="row in monthlyTrackingTableRows" :key="row.key">
                    <th>{{ row.label }}</th>
                    <td v-for="month in monthlyTrackingRows" :key="`${row.key}-${month.key}`" class="is-right">
                      {{ row.format === 'percent' ? formatPercent(month[row.key]) : formatDecimalOrNumber(month[row.key], row.format) }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="isMonthlyTrackingLoading" class="tracking-monthly-loader" aria-live="polite">
                <span class="tracking-monthly-spinner" />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="isDomainsWithoutScheduledContentsOpen"
          class="tracking-modal-overlay"
          @click.self="closeDomainsWithoutScheduledContents"
        >
          <div class="tracking-modal-card">
            <header class="tracking-modal-header">
              <div>
                <h3 class="tracking-modal-title">Domaines sans contenus à venir</h3>
                <p class="tracking-modal-subtitle">
                  Aucun article ni sondage programmé {{ currentWeekSchedulePeriodLabel.toLowerCase() }}.
                </p>
              </div>
              <button
                type="button"
                class="tracking-modal-close"
                aria-label="Fermer la liste des domaines"
                @click="closeDomainsWithoutScheduledContents"
              >
                ×
              </button>
            </header>

            <div class="tracking-modal-table-wrapper">
              <table class="tracking-modal-table">
                <thead>
                  <tr>
                    <th>Domaine</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="domainsWithoutScheduledContents.domains.length === 0">
                    <td>Tous les domaines filtrés ont au moins un contenu à venir.</td>
                  </tr>
                  <tr
                    v-for="domain in domainsWithoutScheduledContents.domains"
                    :key="domain.domain_uuid"
                  >
                    <td>{{ domain.domain_label }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </page-content>
  </div>
  <div v-else class="px-6 py-10">
    <p class="text-sm text-gray-600">
      Vous n'avez pas accès à cette section.
    </p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { debounce } from 'lodash'
import { http } from '@/services/api.service'
import { format, subDays, subMonths, subWeeks } from 'date-fns'
import PageContent from '@/components/PageContent.vue'
import MVPTable from '@/components/agGrid/MVPTable.vue'
import DashboardDateFilters from '@/components/dashboard/DashboardDateFilters.vue'
import { DASHBOARD_QUICK_RANGES } from './state'
import { getFullTrackingDateRange } from './domainPeriod'
import { fetchTrackingDomains } from './trackingDomains'
import { formatTrackingDomainSelectionLabel, getTrackingQueryState, normalizeTrackingDomainUuids, syncTrackingQueryFromVm } from './trackingQuery'

const DATE_FORMAT = 'yyyy-MM-dd'
const MONTH_LABELS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']

function numericComparator (valueA, valueB) {
  return Number(valueA || 0) - Number(valueB || 0)
}

function toIsoDate (date) {
  return format(date, DATE_FORMAT)
}

function createDefaultTotals () {
  return {
    publishedContents: 0,
    duplicatedContents: 0,
    originalContents: 0,
    duplicationRate: 0,
    articleContents: 0,
    faqContents: 0,
    surveyContents: 0,
    articleDuplicatedContents: 0,
    faqDuplicatedContents: 0,
    surveyDuplicatedContents: 0,
    expertContents: 0,
    expertArticleContents: 0,
    expertFaqContents: 0,
    expertSurveyContents: 0,
    mediaContents: 0,
    mediaArticleContents: 0,
    mediaSurveyContents: 0
  }
}

function createDefaultDomainsWithoutScheduledContents () {
  return {
    count: 0,
    domains: [],
    dateStart: null,
    dateEnd: null
  }
}

export default {
  name: 'app-dashboard-tracking-elected-published-content',
  components: {
    PageContent,
    MVPTable,
    DashboardDateFilters
  },
  beforeRouteEnter (to, from, next) {
    next(async vm => {
      const hasAccess = await vm.ensureAccess()
      if (!hasAccess) {
        vm.$router.replace('/app/dashboard')
      }
    })
  },
  data () {
    const today = new Date()
    const defaultEnd = toIsoDate(today)
    const defaultStart = toIsoDate(subDays(today, 29))
    const trackingState = getTrackingQueryState(this.$route.query, {
      activeQuickRangeId: 'month-1',
      dateRange: [defaultStart, defaultEnd],
      selectedDomainUuids: []
    })

    return {
      quickRanges: DASHBOARD_QUICK_RANGES.map(range => ({ ...range })),
      activeQuickRangeId: trackingState.activeQuickRangeId,
      dateRange: trackingState.dateRange,
      maxDate: defaultEnd,
      domainOptions: [],
      selectedDomainUuids: trackingState.selectedDomainUuids,
      selectedType: '',
      selectedDuplication: '',
      selectedTrackingYear: today.getFullYear(),
      totals: createDefaultTotals(),
      domainsWithoutScheduledContents: createDefaultDomainsWithoutScheduledContents(),
      monthlySeries: [],
      gridOptions: null,
      gridData: [],
      gridApi: null,
      totalRowCount: 0,
      isLoading: false,
      isMonthlyTrackingLoading: false,
      isMonthlyTrackingOpen: false,
      isDomainsWithoutScheduledContentsOpen: false,
      statsRequestSequence: 0,
      monthlyRequestSequence: 0,
      activeStatsRequestKey: null,
      activeMonthlyRequestKey: null,
      debouncedLoadStats: null,
      debouncedLoadMonthlyTracking: null,
      isInitializingTracking: true
    }
  },
  computed: {
    ...mapGetters(['roles']),
    hasAccess () {
      return this.roles.includes('admin') || this.roles.includes('community-manager')
    },
    typeOptions () {
      return [
        { value: '', label: 'Tous les contenus' },
        { value: 'article', label: 'Articles' },
        { value: 'faq', label: 'FAQ' },
        { value: 'survey', label: 'Sondages' }
      ]
    },
    duplicationOptions () {
      return [
        { value: '', label: 'Tous les statuts' },
        { value: 'original', label: 'Experts (articles / FAQ / sondages)' },
        { value: 'duplicated', label: 'Médias (articles / sondages)' }
      ]
    },
    displayedDomainCount () {
      return Array.isArray(this.gridData) ? this.gridData.length : 0
    },
    hasDomainFilter () {
      return normalizeTrackingDomainUuids(this.selectedDomainUuids).length > 0
    },
    domainSelectionLabel () {
      return formatTrackingDomainSelectionLabel(this.selectedDomainUuids, this.domainOptions)
    },
    domainSelectKey () {
      return `${this.$route.path}::${this.domainOptions.length}`
    },
    averageExpertContentsPerDomain () {
      return this.averagePerDomain(this.totals.expertContents)
    },
    averageMediaContentsPerDomain () {
      return this.averagePerDomain(this.totals.mediaContents)
    },
    currentWeekSchedulePeriodLabel () {
      const dateStart = this.formatApiDate(this.domainsWithoutScheduledContents.dateStart)
      const dateEnd = this.formatApiDate(this.domainsWithoutScheduledContents.dateEnd)

      return dateStart && dateEnd
        ? `Du ${dateStart} au ${dateEnd}`
        : 'Cette semaine'
    },
    trackingYearOptions () {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let year = currentYear; year >= 2020; year--) {
        years.push(year)
      }
      return years
    },
    monthlyTrackingRows () {
      const rows = Array.isArray(this.monthlySeries) ? this.monthlySeries : []
      return Array.from({ length: 12 }, (_, index) => {
        const monthNumber = index + 1
        const row = rows.find(item => Number(item.month) === monthNumber) || {}
        const isFutureMonth = this.isFutureTrackingMonth(monthNumber)

        return {
          key: monthNumber,
          label: MONTH_LABELS[index],
          allContents: isFutureMonth ? 0 : Number(row.allContents || 0),
          expertAll: isFutureMonth ? 0 : Number(row.expertAll || 0),
          expertArticle: isFutureMonth ? 0 : Number(row.expertArticle || 0),
          expertFaq: isFutureMonth ? 0 : Number(row.expertFaq || 0),
          expertSurvey: isFutureMonth ? 0 : Number(row.expertSurvey || 0),
          expertAverage: isFutureMonth ? 0 : Number(row.expertAverage || 0),
          mediaAll: isFutureMonth ? 0 : Number(row.mediaAll || 0),
          mediaArticle: isFutureMonth ? 0 : Number(row.mediaArticle || 0),
          mediaSurvey: isFutureMonth ? 0 : Number(row.mediaSurvey || 0),
          mediaAverage: isFutureMonth ? 0 : Number(row.mediaAverage || 0),
          duplicationRate: isFutureMonth ? 0 : Number(row.duplicationRate || 0)
        }
      })
    },
    monthlyTrackingTableRows () {
      const rows = [
        { key: 'allContents', label: 'Contenus all', format: 'number', group: 'all', types: ['article', 'faq', 'survey'] },
        { key: 'expertAll', label: 'All expert', format: 'number', group: 'expert', types: ['article', 'faq', 'survey'] },
        { key: 'expertArticle', label: 'Article expert', format: 'number', group: 'expert', types: ['article'] },
        { key: 'expertFaq', label: 'FAQ expert', format: 'number', group: 'expert', types: ['faq'] },
        { key: 'expertSurvey', label: 'Sondage expert', format: 'number', group: 'expert', types: ['survey'] },
        { key: 'expertAverage', label: 'Moyenne expert', format: 'decimal', group: 'expert', types: ['article', 'faq', 'survey'], isAverage: true },
        { key: 'mediaAll', label: 'All média', format: 'number', group: 'media', types: ['article', 'survey'] },
        { key: 'mediaArticle', label: 'Articles média', format: 'number', group: 'media', types: ['article'] },
        { key: 'mediaSurvey', label: 'Sondage média', format: 'number', group: 'media', types: ['survey'] },
        { key: 'mediaAverage', label: 'Moyenne média', format: 'decimal', group: 'media', types: ['article', 'survey'], isAverage: true },
        { key: 'duplicationRate', label: 'Taux de duplication', format: 'percent', group: 'duplication', types: ['article', 'survey'] }
      ]

      const selectedType = this.selectedType || null
      const selectedStatus = this.selectedDuplication || null
      const statusGroup = {
        original: 'expert',
        duplicated: 'media'
      }[selectedStatus]

      return rows.filter(row => {
        if (this.hasDomainFilter && row.isAverage) {
          return false
        }

        if (selectedType && !row.types.includes(selectedType)) {
          return false
        }

        if (statusGroup) {
          if (row.group === 'all') {
            return true
          }

          return row.group === statusGroup
        }

        return true
      })
    }
  },
  watch: {
    selectedDomainUuids: {
      handler () {
        if (this.activeQuickRangeId === 'full') {
          this.setFullDateRange()
        }
        syncTrackingQueryFromVm(this)
        if (this.isInitializingTracking) {
          return
        }
        this.scheduleStatsReload()
        if (this.isMonthlyTrackingOpen) {
          this.scheduleMonthlyTrackingReload()
        }
      },
      deep: true
    },
    selectedType () {
      if (this.isInitializingTracking) {
        return
      }
      this.scheduleStatsReload()
      if (this.isMonthlyTrackingOpen) {
        this.scheduleMonthlyTrackingReload()
      }
    },
    selectedDuplication () {
      if (this.isInitializingTracking) {
        return
      }
      this.scheduleStatsReload()
      if (this.isMonthlyTrackingOpen) {
        this.scheduleMonthlyTrackingReload()
      }
    },
    selectedTrackingYear () {
      if (!this.isInitializingTracking && this.isMonthlyTrackingOpen) {
        this.scheduleMonthlyTrackingReload()
      }
    }
  },
  async created () {
    this.initializeGridOptions()
    this.debouncedLoadStats = debounce(this.loadStats, 400)
    this.debouncedLoadMonthlyTracking = debounce(this.loadMonthlyTracking, 400)

    const hasAccess = await this.ensureAccess()
    if (!hasAccess) {
      this.$router.replace('/app/dashboard')
      return
    }

    const waitForDomains = this.activeQuickRangeId === 'full'
    const domainsPromise = this.fetchDomains()
    if (waitForDomains) {
      await domainsPromise
      this.setFullDateRange()
    }
    syncTrackingQueryFromVm(this)
    this.isInitializingTracking = false
    this.loadStats()
    if (this.isMonthlyTrackingOpen) {
      this.loadMonthlyTracking()
    }
    if (!waitForDomains) {
      await domainsPromise
    }
  },
  beforeDestroy () {
    if (this.debouncedLoadStats) {
      this.debouncedLoadStats.cancel()
    }
    if (this.debouncedLoadMonthlyTracking) {
      this.debouncedLoadMonthlyTracking.cancel()
    }
    this.statsRequestSequence += 1
    this.monthlyRequestSequence += 1
  },
  methods: {
    async ensureAccess () {
      if (!Array.isArray(this.roles) || this.roles.length === 0) {
        try {
          await this.$store.dispatch('FETCH_ROLES')
        } catch (error) {
          return false
        }
      }

      return this.hasAccess
    },
    initializeGridOptions () {
      this.gridOptions = {
        rowModelType: 'clientSide',
        suppressMultiSort: true,
        context: {
          componentParent: this
        },
        pinnedBottomRowData: [],
        getRowClass: params => params?.node?.rowPinned ? 'tracking-total-row' : '',
        defaultColDef: {
          resizable: true,
          sortable: true,
          floatingFilter: false,
          sortingOrder: ['asc', 'desc']
        },
        columnDefs: [
          {
            headerName: 'Domaine',
            field: 'domain_label',
            sortable: true,
            minWidth: 210,
            pinned: 'left'
          },
          {
            headerName: 'Total',
            field: 'published_contents',
            sortable: true,
            minWidth: 120,
            comparator: numericComparator,
            sort: 'desc',
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'Total contenus experts',
            field: 'expert_contents',
            sortable: true,
            minWidth: 190,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'Articles experts',
            field: 'expert_article_contents',
            sortable: true,
            minWidth: 160,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'FAQ',
            field: 'expert_faq_contents',
            sortable: true,
            minWidth: 100,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'Sondage expert',
            field: 'expert_survey_contents',
            sortable: true,
            minWidth: 150,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'Total médias dupliqués',
            field: 'media_contents',
            sortable: true,
            minWidth: 190,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'Article média',
            field: 'media_article_contents',
            sortable: true,
            minWidth: 145,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'Sondage média',
            field: 'media_survey_contents',
            sortable: true,
            minWidth: 150,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'Taux de duplication',
            field: 'duplication_rate',
            sortable: true,
            minWidth: 150,
            comparator: numericComparator,
            valueFormatter: params => this.formatPercent(params.value)
          }
        ]
      }
    },
    async fetchDomains () {
      try {
        const domains = await fetchTrackingDomains()
        this.domainOptions = domains

        const allowedUuids = new Set(domains.map(domain => domain.uuid))
        const currentSelectedDomainUuids = normalizeTrackingDomainUuids(this.selectedDomainUuids)
        const nextSelectedDomainUuids = currentSelectedDomainUuids
          .filter(uuid => allowedUuids.has(uuid))

        if (nextSelectedDomainUuids.join('|') !== currentSelectedDomainUuids.join('|')) {
          this.selectedDomainUuids = nextSelectedDomainUuids
        }
      } catch (error) {
        this.domainOptions = []
      }
    },
    scheduleStatsReload () {
      if (this.debouncedLoadStats) {
        this.debouncedLoadStats()
        return
      }
      this.loadStats()
    },
    scheduleMonthlyTrackingReload () {
      if (this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking()
        return
      }
      this.loadMonthlyTracking()
    },
    loadStats () {
      if (this.isInitializingTracking) {
        return
      }
      if (this.debouncedLoadStats) {
        this.debouncedLoadStats.cancel()
      }

      const params = {
        ...this.buildRequestParams(),
        includeMonthly: 0
      }
      const requestKey = JSON.stringify(params)
      if (this.activeStatsRequestKey === requestKey) {
        return
      }

      const requestSequence = this.statsRequestSequence + 1
      this.statsRequestSequence = requestSequence
      this.activeStatsRequestKey = requestKey
      this.isLoading = true

      http.get('/dashboard/elected-published-contents', { params })
        .then(({ data }) => {
          if (requestSequence !== this.statsRequestSequence) {
            return
          }

          this.totals = this.normalizeTotals(data?.totals)
          this.gridData = Array.isArray(data?.domains) ? data.domains : []
          this.domainsWithoutScheduledContents = this.normalizeDomainsWithoutScheduledContents(
            data?.domainsWithoutScheduledContents
          )
          this.totalRowCount = this.gridData.length
          this.updatePinnedTotals()

          if (this.gridApi) {
            this.gridApi.dispatchEvent({ type: 'totalRowCountUpdated' })
          }
        })
        .catch(() => {
          if (requestSequence !== this.statsRequestSequence) {
            return
          }

          this.totals = createDefaultTotals()
          this.gridData = []
          this.domainsWithoutScheduledContents = createDefaultDomainsWithoutScheduledContents()
          this.totalRowCount = 0
          this.updatePinnedTotals()
          this.$buefy.toast.open({
            message: 'Impossible de charger les statistiques de contenus publiés.',
            type: 'is-danger'
          })
        })
        .finally(() => {
          if (requestSequence === this.statsRequestSequence) {
            this.isLoading = false
            this.activeStatsRequestKey = null
          }
        })
    },
    loadMonthlyTracking () {
      if (this.isInitializingTracking) {
        return
      }
      if (this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking.cancel()
      }

      const params = {
        ...this.buildRequestParams(),
        monthlyOnly: 1
      }
      const requestKey = JSON.stringify(params)
      if (this.activeMonthlyRequestKey === requestKey) {
        return
      }

      const requestSequence = this.monthlyRequestSequence + 1
      this.monthlyRequestSequence = requestSequence
      this.activeMonthlyRequestKey = requestKey
      this.isMonthlyTrackingLoading = true

      http.get('/dashboard/elected-published-contents', { params })
        .then(({ data }) => {
          if (requestSequence !== this.monthlyRequestSequence) {
            return
          }
          this.monthlySeries = Array.isArray(data?.monthlySeries) ? data.monthlySeries : []
        })
        .catch(() => {
          if (requestSequence === this.monthlyRequestSequence) {
            this.monthlySeries = []
          }
        })
        .finally(() => {
          if (requestSequence === this.monthlyRequestSequence) {
            this.isMonthlyTrackingLoading = false
            this.activeMonthlyRequestKey = null
          }
        })
    },
    normalizeTotals (totals) {
      const defaults = createDefaultTotals()
      return Object.keys(defaults).reduce((result, key) => {
        result[key] = Number(totals?.[key] || 0)
        return result
      }, {})
    },
    normalizeDomainsWithoutScheduledContents (summary) {
      const domains = Array.isArray(summary?.domains)
        ? summary.domains.filter(domain => domain?.domain_uuid && domain?.domain_label)
        : []

      return {
        count: domains.length,
        domains,
        dateStart: typeof summary?.dateStart === 'string' ? summary.dateStart : null,
        dateEnd: typeof summary?.dateEnd === 'string' ? summary.dateEnd : null
      }
    },
    buildRequestParams () {
      const params = {
        trackingYear: this.selectedTrackingYear
      }

      if (Array.isArray(this.dateRange) && this.dateRange.length === 2) {
        params.dateStart = this.dateRange[0]
        params.dateEnd = this.dateRange[1]
      }

      const selectedDomainUuids = normalizeTrackingDomainUuids(this.selectedDomainUuids)
      if (selectedDomainUuids.length > 0) {
        params.domains = selectedDomainUuids
      }

      if (this.selectedType) {
        params.type = this.selectedType
      }

      if (this.selectedDuplication) {
        params.duplication = this.selectedDuplication
      }

      return params
    },
    applyQuickRange (range) {
      if (!range) return

      this.activeQuickRangeId = range.id
      const today = new Date()
      let start = null

      if (range.type === 'week') {
        start = subWeeks(today, range.amount)
      } else if (range.type === 'month') {
        start = subMonths(today, range.amount)
      } else if (range.type === 'day') {
        start = subDays(today, range.amount - 1)
      }

      if (!start) {
        start = subDays(today, 29)
      }

      this.dateRange = [toIsoDate(start), toIsoDate(today)]
      syncTrackingQueryFromVm(this)
      this.loadStats()
    },
    showFull () {
      this.activeQuickRangeId = 'full'
      this.setFullDateRange()
      syncTrackingQueryFromVm(this)
      this.loadStats()
    },
    setFullDateRange () {
      this.dateRange = getFullTrackingDateRange(this.domainOptions, this.selectedDomainUuids)
      this.maxDate = this.dateRange[1]
    },
    handleDateRangeUpdate (range) {
      if (!Array.isArray(range) || range.length !== 2) {
        this.dateRange = null
      } else {
        this.dateRange = [...range]
      }

      this.activeQuickRangeId = null
      syncTrackingQueryFromVm(this)
      this.scheduleStatsReload()
    },
    onGridReady (params) {
      this.gridApi = params.api
      this.updatePinnedTotals()
      this.gridApi.dispatchEvent({ type: 'totalRowCountUpdated' })
    },
    updatePinnedTotals () {
      if (!this.gridApi) {
        return
      }

      const rows = Array.isArray(this.gridData) ? this.gridData : []
      if (!rows.length) {
        this.gridApi.setPinnedBottomRowData([])
        return
      }

      const sumFields = [
        'published_contents',
        'expert_contents',
        'expert_article_contents',
        'expert_faq_contents',
        'expert_survey_contents',
        'media_contents',
        'media_article_contents',
        'media_survey_contents'
      ]

      const totalsRow = sumFields.reduce((result, field) => {
        result[field] = rows.reduce((sum, row) => sum + Number(row?.[field] || 0), 0)
        return result
      }, {
        domain_label: 'Total'
      })

      totalsRow.duplication_rate = rows.reduce((sum, row) => sum + Number(row?.duplication_rate || 0), 0) / rows.length

      this.gridApi.setPinnedBottomRowData([totalsRow])
    },
    averagePerDomain (value) {
      const domainsCount = this.displayedDomainCount
      if (domainsCount <= 0) {
        return 0
      }

      return Number(value || 0) / domainsCount
    },
    isFutureTrackingMonth (monthNumber) {
      const today = new Date()
      const currentYear = today.getFullYear()
      const currentMonth = today.getMonth() + 1

      return this.selectedTrackingYear > currentYear ||
        (this.selectedTrackingYear === currentYear && Number(monthNumber) > currentMonth)
    },
    formatNumber (value) {
      return new Intl.NumberFormat('fr-FR').format(Number(value || 0))
    },
    formatPercent (value) {
      return `${new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(Number(value || 0))}%`
    },
    formatDecimal (value) {
      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(Number(value || 0))
    },
    formatDecimalOrNumber (value, format) {
      if (format === 'decimal') {
        return this.formatDecimal(value)
      }

      return this.formatNumber(value)
    },
    formatApiDate (value) {
      const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
      return match ? `${match[3]}/${match[2]}/${match[1]}` : null
    },
    openDomainsWithoutScheduledContents () {
      this.isDomainsWithoutScheduledContentsOpen = true
    },
    closeDomainsWithoutScheduledContents () {
      this.isDomainsWithoutScheduledContentsOpen = false
    },
    openMonthlyTracking () {
      this.isMonthlyTrackingOpen = true
      this.monthlySeries = []
      if (!this.isInitializingTracking) {
        this.loadMonthlyTracking()
      }
    },
    closeMonthlyTracking () {
      this.isMonthlyTrackingOpen = false
      if (this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking.cancel()
      }
    }
  }
}
</script>

<style scoped>
.tracking-filter-field {
  min-width: 11rem;
  flex: 1 1 11rem;
  display: flex;
  align-items: center;
}

.tracking-filter-field--wide {
  min-width: 16rem;
  flex: 1.3 1 16rem;
}

.published-overview-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(0, 1.45fr) repeat(2, minmax(14rem, 0.7fr));
  gap: 1rem;
}

.published-space {
  position: relative;
  overflow: hidden;
}

.published-space-header {
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  height: 5.25rem;
  padding: 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.published-space-header h2 {
  margin: 0;
  color: #111827;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.35;
}

.published-card-body {
  display: grid;
  grid-template-columns: minmax(12rem, max-content) auto;
  align-items: center;
  justify-content: space-between;
  gap: clamp(2rem, 4vw, 4rem);
  min-height: 11rem;
  padding: 1.25rem;
}

.published-breakdown {
  display: grid;
  gap: 0.75rem;
  margin: 0 0 0 1rem;
  padding: 0;
  min-width: min(100%, 21rem);
}

.published-breakdown--compact {
  align-self: center;
}

.published-breakdown-row {
  position: relative;
  display: grid;
  grid-template-columns: 5.25rem 3.75rem;
  align-items: baseline;
  justify-content: start;
  gap: 1.5rem;
  padding: 0.45rem 0;
}

.published-breakdown-row::before {
  position: absolute;
  top: 50%;
  left: -0.05rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  content: '';
  transform: translate(-1rem, -50%);
  background: #94a3b8;
}

.published-breakdown-row--article::before {
  background: #2563eb;
}

.published-breakdown-row--faq::before {
  background: #7c3aed;
}

.published-breakdown-row--survey::before {
  background: #0f766e;
}

.published-breakdown dt {
  color: #4b5563;
  font-size: 0.875rem;
  font-weight: 500;
}

.published-breakdown dd {
  justify-self: end;
  margin: 0;
  color: #111827;
  font-size: 0.875rem;
  font-weight: 700;
}

.published-space--expert .published-average-circle {
  border-color: #dbeafe;
  box-shadow: 0 0 0 1px #bfdbfe;
}

.published-space--expert .published-average-circle span {
  color: #2563eb;
}

.published-space--media .published-average-circle {
  border-color: #dbeafe;
  box-shadow: 0 0 0 1px #bfdbfe;
}

.published-space--media .published-average-circle span {
  color: #2563eb;
}

.published-average-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-width: 8rem;
  text-align: center;
}

.published-average-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 7.5rem;
  height: 7.5rem;
  border: 12px solid #e5e7eb;
  border-radius: 9999px;
  background: #ffffff;
  box-shadow: 0 0 0 1px #e5e7eb;
}

.published-average-circle span {
  color: #111827;
  font-size: 1.35rem;
  font-weight: 700;
}

.published-average-card p {
  margin: 0;
  color: #4b5563;
  font-size: 0.875rem;
}

.published-duplication-card,
.published-unscheduled-card {
  min-height: 14rem;
}

.published-duplication-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 9rem;
  gap: 0.6rem;
  padding: 1.25rem;
  text-align: center;
  background: #eff6ff;
}

.published-duplication-body strong {
  color: #111827;
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1;
}

.published-duplication-body span {
  color: #4b5563;
  font-size: 0.875rem;
}

.published-unscheduled-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  width: 100%;
  padding: 0;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;
}

.published-unscheduled-card .published-space-header {
  flex: 0 0 5.25rem;
  height: 5.25rem;
}

.published-unscheduled-card .published-space-header h2 {
  font-size: 0.875rem;
  line-height: 1.3;
}

.published-unscheduled-card:hover {
  border-color: #f59e0b;
  box-shadow: 0 10px 20px rgba(146, 64, 14, 0.12);
  transform: translateY(-1px);
}

.published-unscheduled-card:focus-visible {
  outline: 3px solid rgba(245, 158, 11, 0.35);
  outline-offset: 2px;
}

.published-unscheduled-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 9rem;
  gap: 0.55rem;
  padding: 1.25rem;
  text-align: center;
  background: linear-gradient(180deg, #fffbeb 0%, #ffffff 62%);
}

.published-unscheduled-body strong {
  color: #b45309;
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1;
}

.published-unscheduled-body > span {
  color: #4b5563;
  font-size: 0.875rem;
}

.published-unscheduled-body .published-unscheduled-link {
  color: #b45309;
  font-weight: 700;
}

@media (max-width: 1536px) {
  .published-overview-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .published-overview-grid,
  .published-card-body {
    grid-template-columns: 1fr;
  }

  .published-space-header,
  .tracking-modal-header {
    height: auto;
    min-height: 5.25rem;
    flex-direction: column;
  }

  .tracking-modal-domain-filter {
    width: 100%;
    margin-left: 0;
  }

  .tracking-period-stat {
    align-items: flex-start;
  }

  .published-average-card {
    justify-self: center;
  }
}

/* Géométrie propre à cette page ; le reste vient de assets/scss/tracking.scss. */
.tracking-modal-card {
  max-height: min(720px, calc(100vh - 3rem));
}

.tracking-modal-card--wide {
  width: min(1180px, 100%);
}

.tracking-monthly-table,
.tracking-monthly-loader {
  min-width: 980px;
}
</style>
